import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { BugCandidate } from '../types/bug';

export interface BugSynthesisFilters {
  status?: 'open' | 'new' | 'all';
  platform?: string;
  team?: string;
}

export interface BugSynthesisStats {
  /** Nombre de signalements bruts analyses. */
  bugCount: number;
  /** Nombre de candidats distincts proposes apres regroupement. */
  candidateCount: number;
  criticalCount: number;
  platformCounts: Record<string, number>;
}

export interface BugSynthesisMetadata {
  generatedAt: string;
  filters: {
    status: string;
    platform: string;
    team: string;
  };
  versions: Record<string, string | null>;
  tokensUsed: number;
}

/**
 * L'analyse ne rend plus de prose : elle propose des candidats que l'admin
 * valide un par un. Rien n'est persiste tant qu'un humain n'a pas promu.
 */
export interface BugSynthesisResult {
  success: boolean;
  candidates: BugCandidate[];
  stats: BugSynthesisStats;
  metadata: BugSynthesisMetadata;
  message?: string;
}

/** Contexte Sentry porte par un candidat detecte sans signalement. */
export interface ContentTag {
  key: string;
  values: { value: string; count: number }[];
}

/** Contenu concerné, tel qu'un seul événement le rapporte. */
export interface ContentSample {
  method: string;
  ref: string | null;
  lesson: string | null;
  isRevision: boolean;
  /** Fichier que l'app n'a pas trouvé, extrait du message d'erreur. */
  file: string | null;
}

export interface SentryCandidateInfo {
  issueId: string;
  shortId: string;
  url: string;
  priority: string;
  level: string;
  events: number;
  users: number;
  culprit: string | null;
  /** null quand le culprit est vide : origine indeterminee, a verifier. */
  culpritIsOurs: boolean | null;
  /** Méthode, leçon, phrase concernées quand l'app a posé les tags.
   *  Sans ça, une issue sur 178 utilisateurs n'est pas actionnable. */
  contentTags?: ContentTag[];
  /** Erreurs de contenu qui ne posent aucun tag `qcm.*` : le relevé est alors
   *  bâti sur les données additionnelles des événements. */
  contentErrorTypes?: string[];
  /** Contenu du dernier événement, quand les tags n'en disent rien. Un
   *  échantillon : les données additionnelles ne sont pas indexées, seul le
   *  relevé complet parcourt les événements. */
  latestContent?: ContentSample | null;
  /** Dates ISO renvoyées par Sentry. `lastSeen` dit si le bug se produit
   *  encore, ou s'il s'est tu depuis une version. */
  firstSeen?: string | null;
  lastSeen?: string | null;
}

/** Un avis de boutique, tel qu'il fonde un candidat. */
export interface ReviewCitation {
  id: number;
  store: string;
  rating: number;
  locale: string | null;
  appVersion: string | null;
  /** Faux pour l'App Store, qui n'attache pas de version a ses avis : la
   *  version affichee est alors deduite de la date, pas declaree. */
  versionKnown: boolean;
  title: string | null;
  body: string;
  reviewDate: number | null;
}

export interface ReviewCandidate extends BugCandidate {
  reviews: ReviewCitation[];
}

export interface ReviewProposal {
  candidates: ReviewCandidate[];
  scanned: number;
  version: string | null;
}

export interface SentryCandidate extends BugCandidate {
  sentry: SentryCandidateInfo;
}

export interface SentryProposal {
  candidates: SentryCandidate[];
  /** Ce qui a ete ecarte, et pourquoi : une liste courte sans explication
   *  laisse croire qu'il n'y avait rien d'autre. */
  excluded: { byPriority: number; byCulprit: number; alreadyTracked: number; alreadyFixedInCode: number; ignored: number };
  scanned: number;
  statsPeriod: string;
  versions: string[];
}

export interface CodeCheckResult {
  /** fixed : un commit cite la clé Sentry — preuve, pas indice.
   *  maybe_fixed : le fichier a bougé depuis la dernière occurrence.
   *  still_present : le code fautif est intact.
   *  gone : le fichier n'existe plus. unknown : rien à chercher. */
  verdict: 'fixed' | 'maybe_fixed' | 'still_present' | 'gone' | 'unknown';
  reasons: string[];
  commits: { hash: string; shortHash: string; message: string; date: string; url: string }[];
  file: string | null;
  lastSeen: string | null;
}

export async function checkBugInCode(sentryShortId: string): Promise<CodeCheckResult> {
  const fn = httpsCallable<{ sentryShortId: string }, CodeCheckResult>(
    functions,
    'checkBugInCode',
    {timeout: 120000},
  );
  const result = await fn({sentryShortId});
  return result.data;
}

/** L'exclusion vit dans Firestore, pas dans Sentry : le jeton Sentry reste
 *  en lecture seule et la vue de l'équipe n'est pas modifiée. */
export async function ignoreSentryIssue(sentryShortId: string, reason: string): Promise<void> {
  const fn = httpsCallable<{ sentryShortId: string; reason: string }, { ignored: string }>(
    functions,
    'ignoreSentryIssue',
  );
  await fn({sentryShortId, reason});
}

export interface ContentReport {
  report: string;
  stats: {
    methods: number; items: number; events: number;
    /** Vrai quand le relevé est partiel : à dire, jamais à taire. */
    truncated: boolean; untagged: number;
  };
}

/** Bâtit le relevé destiné à l'équipe contenu : pour chaque méthode, les
 *  leçons et les phrases concernées. */
export async function buildContentReport(sentryShortId: string): Promise<ContentReport> {
  const fn = httpsCallable<{ sentryShortId: string }, ContentReport>(
    functions,
    'buildContentReport',
    {timeout: 180000},
  );
  const result = await fn({sentryShortId});
  return result.data;
}

/**
 * Publie le relevé dans le canal Slack.
 *
 * Le texte part tel qu'il est affiché : c'est l'appelant qui le fournit, la
 * fonction ne le reconstruit pas. Sinon un écart s'installerait entre ce qui
 * a été relu et ce qui est publié.
 */
export async function sendContentReportToSlack(
  sentryShortId: string,
  report: string,
): Promise<{ posted: boolean; blocks: number }> {
  const fn = httpsCallable<{ sentryShortId: string; report: string }, { posted: boolean; blocks: number }>(
    functions,
    'sendContentReportToSlack',
    {timeout: 60000},
  );
  const result = await fn({sentryShortId, report});
  return result.data;
}

export async function proposeReviewBugs(version?: string): Promise<ReviewProposal> {
  const fn = httpsCallable<{ version?: string }, ReviewProposal>(
    functions,
    'proposeReviewBugs',
    // L'analyse lit jusqu'a trois cents avis et les passe au modele : plus
    // longue que le rapprochement Sentry, qui ne fait que des requetes.
    {timeout: 300000},
  );
  const result = await fn({version});
  return result.data;
}

export async function proposeSentryBugs(statsPeriod = '14d'): Promise<SentryProposal> {
  const fn = httpsCallable<{ statsPeriod: string }, SentryProposal>(
    functions,
    'proposeSentryBugs',
    {timeout: 120000},
  );
  const result = await fn({statsPeriod});
  return result.data;
}

export async function generateBugSynthesis(
  filters: BugSynthesisFilters
): Promise<BugSynthesisResult> {
  const generateFn = httpsCallable<BugSynthesisFilters, BugSynthesisResult>(
    functions,
    'generateBugSynthesis',
    // L'extraction sur un gros lot depasse souvent la minute.
    { timeout: 300000 }
  );
  const result = await generateFn(filters);
  return result.data;
}
